import { z } from 'zod';
/**
 * Schéma "identité" — sous-ensemble du profil étudiant manipulé en
 * Phase 2.2 (premier onglet). Les onglets suivants (expériences, diplômes,
 * compétences, langues) viennent dans des schémas dédiés pour permettre
 * une sauvegarde indépendante par section.
 */
export const studentIdentitySchema = z.object({
    civilite: z.enum(['M', 'Mme', 'X']).optional().or(z.literal('')),
    // V2 (mai 2026) — sauvegarde tolérée même si vide : un candidat peut
    // enregistrer un brouillon incomplet. La complétude est vérifiée plus
    // tard côté `/publication` (status = 'published').
    nom: z.string().trim().max(100).default(''),
    prenom: z.string().trim().max(100).default(''),
    dateNaissance: z
        .union([z.coerce.date(), z.literal(''), z.null()])
        .optional()
        .transform((v) => (v === '' || v === null ? undefined : v)),
    telephone: z
        .string()
        .trim()
        .regex(/^\+?[\d\s().-]{6,20}$/, 'Numéro de téléphone invalide.')
        .optional()
        .or(z.literal('')),
    pitch: z.string().max(800).optional().or(z.literal('')),
    // Vague A — réseau social pro. Optionnel, validation URL si fourni.
    // V2 (mai 2026) — `preprocess` enlève les espaces internes (cas typique
    // d'une URL extraite d'un PDF avec un retour de ligne au milieu :
    // "linkedin.com/in/pi erre-..." devient "linkedin.com/in/pierre-...").
    // V3 (mai 2026) — auto-préfixe https:// si l'URL n'a pas de protocole.
    // L'IA extrait souvent "www.linkedin.com/..." sans "https://" — Zod
    // .url() rejette ces valeurs, ce qui causait un 400 silencieux.
    linkedinUrl: z.preprocess((v) => {
        if (typeof v !== 'string')
            return v;
        const cleaned = v.replace(/\s+/g, '');
        if (cleaned && !/^https?:\/\//i.test(cleaned))
            return `https://${cleaned}`;
        return cleaned;
    }, z
        .string()
        .url('URL LinkedIn invalide.')
        .max(500)
        // Rejeter explicitement les protocoles dangereux (javascript:, data:, etc.).
        // Le preprocess ci-dessus préfixe déjà https:// si absent, mais un attaquant
        // peut fournir directement "javascript:..." — .url() l'accepte car il est
        // syntaxiquement valide selon la RFC 3986.
        .refine((url) => /^https?:\/\//i.test(url), { message: "L'URL doit commencer par http:// ou https://." })
        .optional()
        .or(z.literal(''))),
});
/**
 * Niveaux de langue acceptés.
 *
 * - Niveaux officiels CECRL : A1 → C2 + `natif` (langue maternelle).
 * - Niveaux informels (mai 2026) : pour les candidats qui n'ont jamais
 *   passé d'évaluation officielle mais veulent quand même renseigner
 *   leur niveau de manière honnête. Ex : « j'ai grandi avec le créole,
 *   je le parle mais ne l'écris pas ».
 *
 * L'ordre est important pour l'UI (les niveaux officiels viennent en
 * premier, les informels après un séparateur visuel).
 */
export const NIVEAUX_LANGUE = [
    'A1',
    'A2',
    'B1',
    'B2',
    'C1',
    'C2',
    'natif',
    // Niveaux informels — pas d'évaluation officielle
    'parle',
    'lu',
    'parle_lu',
    'lu_parle_ecrit',
];
/** Libellés FR pour l'UI. */
export const NIVEAU_LANGUE_LABELS = {
    A1: 'A1 — Découverte',
    A2: 'A2 — Élémentaire',
    B1: 'B1 — Intermédiaire',
    B2: 'B2 — Avancé',
    C1: 'C1 — Autonome',
    C2: 'C2 — Maîtrise',
    natif: 'Langue maternelle',
    parle: 'Parlé',
    lu: 'Lu',
    parle_lu: 'Parlé et lu',
    lu_parle_ecrit: 'Lu, parlé et écrit',
};
/** Sépare les niveaux officiels (CECRL + natif) des niveaux informels.
 *  Utile pour rendre un `<optgroup>` dans l'UI. */
export const NIVEAUX_LANGUE_OFFICIELS = [
    'A1',
    'A2',
    'B1',
    'B2',
    'C1',
    'C2',
    'natif',
];
export const NIVEAUX_LANGUE_INFORMELS = [
    'parle',
    'lu',
    'parle_lu',
    'lu_parle_ecrit',
];
export const langueSchema = z.object({
    code: z.string().trim().min(2).max(5),
    niveau: z.enum(NIVEAUX_LANGUE),
});
export const experienceSchema = z
    .object({
    intitule: z.string().trim().min(1).max(200),
    entreprise: z.string().trim().min(1).max(200),
    dateDebut: z.coerce.date(),
    dateFin: z.union([z.coerce.date(), z.literal(''), z.null()]).optional(),
    description: z.string().max(1000).optional().or(z.literal('')),
})
    .transform((v) => ({
    ...v,
    dateFin: v.dateFin === '' || v.dateFin === null ? undefined : v.dateFin,
}));
export const diplomeSchema = z.object({
    intitule: z.string().trim().min(1).max(200),
    etablissement: z.string().trim().min(1).max(200),
    annee: z.coerce.number().int().min(1950).max(2100),
});
/** Onglet "Parcours" — diplômes + expériences */
export const studentParcoursSchema = z.object({
    diplomes: z.array(diplomeSchema).max(10).default([]),
    experiences: z.array(experienceSchema).max(20).default([]),
});
/** Onglet "Compétences" — langues + compétences techniques + mobilité géographique */
export const studentCompetencesSchema = z.object({
    langues: z.array(langueSchema).max(10).default([]),
    competences: z.array(z.string().trim().min(1).max(80)).max(40).default([]),
    mobiliteGeographique: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
});
/**
 * Schéma de réponse "extraction CV → profil" — Bloc 3.
 *
 * Tous les champs sont optionnels : le LLM ne devine pas, il omet quand il
 * ne trouve pas. Côté UI, on présente le tout dans une modale de revue et
 * l'utilisateur coche/décoche ce qu'il veut écraser.
 */
export const cvExtractedProfileSchema = z.object({
    identite: z
        .object({
        civilite: z.enum(['M', 'Mme', 'X']).optional(),
        nom: z.string().trim().max(100).optional(),
        prenom: z.string().trim().max(100).optional(),
        dateNaissance: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
        telephone: z.string().trim().max(40).optional(),
        email: z.string().trim().email().max(200).optional(),
        ville: z.string().trim().max(80).optional(),
        codePostal: z.string().trim().max(10).optional(),
        linkedinUrl: z.string().trim().url().max(500).optional(),
    })
        .optional(),
    pitch: z.string().trim().max(800).optional(),
    diplomes: z
        .array(z.object({
        intitule: z.string().trim().min(1).max(200),
        etablissement: z.string().trim().min(1).max(200),
        annee: z.number().int().min(1950).max(2100),
    }))
        .max(10)
        .optional(),
    experiences: z
        .array(z.object({
        intitule: z.string().trim().min(1).max(200),
        entreprise: z.string().trim().min(1).max(200),
        dateDebut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
        dateFin: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
        description: z.string().trim().max(1000).optional(),
    }))
        .max(20)
        .optional(),
    langues: z.array(langueSchema).max(15).optional(),
    competences: z.array(z.string().trim().min(1).max(80)).max(40).optional(),
});
/**
 * Body de l'endpoint POST /etudiant/profile/extract-from-cv.
 * Si cvId n'est pas fourni, le serveur prend le CV le plus récent.
 */
export const extractProfileFromCvSchema = z.object({
    cvId: z.string().uuid().optional(),
});
/** Sélection des secteurs ciblés (1 ou plus, jusqu'à tous les référentiels). */
export const studentSectorsSchema = z.object({
    sectorIds: z
        .array(z.string().uuid())
        .min(1, 'Sélectionnez au moins 1 secteur.')
        .max(50, 'Trop de secteurs sélectionnés.'),
});
// ─── Vague C — onglet "Mon projet" (bonus, optionnel) ────────────────────
//
// Le candidat peut écrire une accroche courte (`pitch`, déjà existant), une
// lettre projet libre et un texte sur ce qu'il cherche idéalement. Tous
// les champs sont optionnels — l'onglet est marqué "bonus" dans la barre
// de progression.
export const studentProjectSchema = z.object({
    pitch: z.string().max(800).optional().or(z.literal('')),
    projectStatement: z.string().max(4000).optional().or(z.literal('')),
    whatImLookingFor: z.string().max(1500).optional().or(z.literal('')),
});
// ─── Vague C — onglet "Préférences" (obligatoire) ────────────────────────
//
// Disponibilité, horaires acceptés, mobilité, salaire min privé, types
// d'établissement souhaités, types de contrat recherchés, secteurs.
// La validation détaillée des codes (sub-sectors, schedules, kinds) reste
// libre (string) — la cohérence avec les référentiels est vérifiée
// côté serveur via une intersection avec les constants.
export const candidateAvailabilitySchema = z.enum([
    'immediate',
    'in_weeks',
    'end_of_studies',
    'saison_ete',
    'saison_hiver',
]);
export const desiredDurationSchema = z.enum(['short', 'medium', 'long', 'permanent']);
export const mobilityRangeSchema = z.enum([
    'local',
    'km50',
    'km100',
    'km200',
    'regional',
    'national',
    'international',
]);
export const studentPreferencesSchema = z.object({
    // Type(s) de contrat recherché(s).
    lookingForJobTypes: z
        .array(z.enum([
        'stage',
        'alternance',
        'apprentissage',
        'cdd',
        'cdi',
        'interim',
        'extra',
        'freelance',
        'autre',
    ]))
        .max(9)
        .default([]),
    // Disponibilité
    availabilityType: candidateAvailabilitySchema.optional().nullable(),
    availabilityStart: z.coerce.date().optional().nullable(),
    desiredDuration: desiredDurationSchema.optional().nullable(),
    // Horaires acceptés (codes libres, validés côté serveur).
    acceptedSchedules: z.array(z.string().trim().max(40)).max(20).default([]),
    // Permis et véhicule.
    hasPermisB: z.boolean().default(false),
    hasVehicle: z.boolean().default(false),
    // Mobilité.
    mobilityRange: mobilityRangeSchema.optional().nullable(),
    mobilityCountries: z.array(z.string().trim().max(60)).max(20).default([]),
    // Salaire minimum souhaité — privé. Stocké en € brut /mois. NULL = non
    // communiqué. Affiche au candidat un message "utilisé pour le matching
    // uniquement, jamais montré aux recruteurs".
    salaryMinMonthly: z
        .number()
        .int()
        .min(0)
        .max(50000)
        .optional()
        .nullable(),
    // Types d'établissement souhaités (codes libres, validés côté serveur).
    establishmentKinds: z.array(z.string().trim().max(40)).max(20).default([]),
});
/**
 * Mise à jour du statut de publication candidat.
 *
 * V1.0 (mai 2026) — accepte les nouveaux statuts `paused` / `placed` /
 * `archived` en plus du legacy `fulfilled` (traité comme alias de `paused`
 * pendant la phase expand/contract). Cf. docs/cahier-des-charges-v3.md §5.1.
 */
export const studentStatusSchema = z.object({
    status: z.enum(['draft', 'published', 'paused', 'placed', 'archived', 'fulfilled']),
    consentVisibility: z.boolean().optional(),
});
// ─── V7 (mai 2026) — Statut "scolaire" du candidat ────────────────────────
//
// in_training      → encore en formation, doit indiquer son établissement.
// looking_for_job  → en recherche d'emploi, établissement optionnel.
// employed         → en poste, établissement optionnel.
//
// Côté serveur, on enforce :
//   - SI educationStatus='in_training' ET PlatformSettings
//     .requireEstablishmentForStudentsInTraining=true
//   - ALORS establishmentId est obligatoire.
//
// La règle "1 candidat = 1 formation principale" (Promotion) reste
// indépendante : un candidat in_training a déjà sa formation via sa
// promotion. L'establishmentId est juste le lien direct (utile pour les
// candidats non-scolaires qui choisissent une formation "HORS-FORMATION"
// mais veulent quand même indiquer leur école d'origine).
export const studentEducationSchema = z
    .object({
    educationStatus: z
        .enum(['in_training', 'looking_for_job', 'employed'])
        .optional()
        .or(z.literal(''))
        .transform((v) => (v ? v : undefined)),
    establishmentId: z
        .string()
        .uuid()
        .optional()
        .or(z.literal(''))
        .transform((v) => (v ? v : undefined)),
})
    .superRefine((v, ctx) => {
    // Si le candidat se déclare en formation et que la plateforme l'exige
    // (côté serveur on revalide en fonction des PlatformSettings),
    // l'établissement est obligatoire. La validation finale stricte est
    // côté API ; ici on ne fait qu'une indication.
    if (v.educationStatus === 'in_training' && !v.establishmentId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Indiquez votre établissement (CFA, lycée, université…) si vous êtes encore en formation.",
            path: ['establishmentId'],
        });
    }
});
/** Profil étudiant complet (utile pour l'écriture en BDD côté API). */
export const studentProfileSchema = studentIdentitySchema.extend({
    mobiliteGeographique: z.array(z.string()).default([]),
    langues: z.array(langueSchema).default([]),
    experiences: z.array(experienceSchema).default([]),
    diplomes: z.array(diplomeSchema).default([]),
    competences: z.array(z.string().min(1).max(80)).max(40).default([]),
    sectorIds: z.array(z.string().uuid()).min(1).max(50, 'Trop de secteurs sélectionnés.'),
});
export const LANGUES_COURANTES = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'Anglais' },
    { code: 'es', label: 'Espagnol' },
    { code: 'de', label: 'Allemand' },
    { code: 'it', label: 'Italien' },
    { code: 'pt', label: 'Portugais' },
    { code: 'zh', label: 'Chinois (mandarin)' },
    { code: 'ja', label: 'Japonais' },
    { code: 'ar', label: 'Arabe' },
    { code: 'ru', label: 'Russe' },
];
// ─── Mobilité géographique — Vague E ──────────────────────────────────────
//
// Référentiel élargi par catégorie. Le candidat coche autant qu'il veut
// dans chaque catégorie ; toutes les valeurs cochées (label exact, ex.
// "Paris" ou "Île-de-France" ou "France entière") sont stockées dans le
// même tableau `mobiliteGeographique` côté Student.
//
// Rétrocompatibilité : les anciennes valeurs ("Toulouse", "Occitanie",
// "France", "Europe", "International") restent valides — elles sont
// présentes dans la nouvelle liste sous "Métropoles françaises", "Régions",
// ou "Échelles élargies".
export const MOBILITE_VILLES = [
    'Paris',
    'Lyon',
    'Marseille',
    'Toulouse',
    'Nice',
    'Bordeaux',
    'Lille',
    'Nantes',
    'Strasbourg',
    'Montpellier',
    'Rennes',
    'Reims',
    'Toulon',
    'Grenoble',
    'Saint-Étienne',
    'Le Havre',
    'Dijon',
    'Angers',
    'Tours',
    'Clermont-Ferrand',
    'Aix-en-Provence',
    'Cannes',
    'Annecy',
    'Biarritz',
    "Saint-Tropez",
    "Courchevel",
    "Megève",
    'Deauville',
    'La Baule',
    'Ajaccio',
];
export const MOBILITE_REGIONS = [
    'Auvergne-Rhône-Alpes',
    'Bourgogne-Franche-Comté',
    'Bretagne',
    'Centre-Val de Loire',
    'Corse',
    'Grand Est',
    'Hauts-de-France',
    'Île-de-France',
    'Normandie',
    'Nouvelle-Aquitaine',
    'Occitanie',
    'Pays de la Loire',
    "Provence-Alpes-Côte d'Azur",
];
export const MOBILITE_ECHELLES = [
    'France entière',
    'France métropolitaine',
    'Outre-mer',
    'Europe',
    'Pays francophones',
    'International',
];
/**
 * Liste à plat (legacy) — utilisée si on veut un picker non-hiérarchique.
 * Les composants UI peuvent préférer les 3 catégories séparées.
 */
export const MOBILITE_OPTIONS = [
    ...MOBILITE_VILLES,
    ...MOBILITE_REGIONS,
    ...MOBILITE_ECHELLES,
];
export const publishStudentSchema = z.object({
    // Cf. studentStatusSchema — mêmes valeurs autorisées (migration V1.0).
    status: z.enum(['draft', 'published', 'paused', 'placed', 'archived', 'fulfilled']),
    consentVisibility: z.boolean().optional(),
});
//# sourceMappingURL=student.js.map